import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, ChevronRight, ArrowUpDown } from "lucide-react";
import { fechaCorta } from "@/lib/format";
import { toast } from "sonner";

export const TIPOS_DOCUMENTO = [
  { value: "DNI", label: "DNI" },
  { value: "PASAPORTE", label: "Pasaporte" },
  { value: "PERMISO_TRABAJO", label: "Permiso de trabajo" },
];
export const tipoDocumentoLabel = (v) =>
  TIPOS_DOCUMENTO.find((o) => o.value === v)?.label || "DNI";

const empty = { nombre: "", apellidos: "", nacionalidad: "ESPAÑOLA", tipo_documento: "DNI", dni: "", direccion: "", telefono: "", email: "" };

export default function Tenants() {
  const { selectedId } = useProperty();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [sort, setSort] = useState({ key: "nombre", dir: "asc" });

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const load = () => {
    if (!selectedId) return setLoading(false);
    setLoading(true);
    api.get(`/tenants?property_id=${selectedId}`).then((r) => setTenants(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [selectedId]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...empty, ...t }); setEditId(t.id); setOpen(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    const payload = { ...form, property_id: selectedId };
    try {
      if (editId) await api.put(`/tenants/${editId}`, payload);
      else await api.post("/tenants", payload);
      toast.success(editId ? "Inquilino actualizado" : "Inquilino creado");
      setOpen(false);
      load();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar inquilino y sus contratos/pagos?")) return;
    await api.delete(`/tenants/${id}`);
    toast.success("Inquilino eliminado");
    load();
  };

  const filtered = tenants.filter((t) =>
    `${t.nombre} ${t.apellidos} ${t.dni}`.toLowerCase().includes(q.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let av, bv;
    if (sort.key === "nombre") { av = `${a.nombre} ${a.apellidos}`; bv = `${b.nombre} ${b.apellidos}`; }
    else if (sort.key === "alta") { av = a.created_at || ""; bv = b.created_at || ""; }
    else { av = a[sort.key] || ""; bv = b[sort.key] || ""; }
    const cmp = String(av).localeCompare(String(bv), "es", { numeric: true });
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const SortHead = ({ k, children, className = "" }) => (
    <TableHead className={className}>
      <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground transition-colors" data-testid={`sort-${k}`}>
        {children}
        <ArrowUpDown className={`h-3.5 w-3.5 ${sort.key === k ? "text-primary" : "text-muted-foreground/50"}`} />
      </button>
    </TableHead>
  );

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  return (
    <div data-testid="tenants-page">
      <PageHeader title="Inquilinos" subtitle={`${tenants.length} registrados`}>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 w-56" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenant-search" />
        </div>
        <Button onClick={openNew} data-testid="add-tenant-button"><Plus className="h-4 w-4 mr-1.5" /> Inquilino</Button>
      </PageHeader>

      <div className="data-card overflow-hidden">
        <Table data-testid="tenants-table">
          <TableHeader>
            <TableRow>
              <SortHead k="nombre">Nombre</SortHead>
              <SortHead k="nacionalidad" className="hidden lg:table-cell">Nacionalidad</SortHead>
              <TableHead>Documento</TableHead>
              <SortHead k="alta">Fecha alta</SortHead>
              <TableHead className="hidden lg:table-cell">Teléfono</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
            ) : sorted.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sin inquilinos</TableCell></TableRow>
            ) : (
              sorted.map((t) => (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => navigate(`/inquilinos/${t.id}`)} data-testid={`tenant-row-${t.id}`}>
                  <TableCell className="font-medium">{t.nombre} {t.apellidos}</TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">{t.nacionalidad}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {t.dni ? (
                      <>
                        <span className="text-muted-foreground text-xs mr-1">{tipoDocumentoLabel(t.tipo_documento)}</span>
                        {t.dni}
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">{fechaCorta(t.created_at)}</TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">{t.telefono || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(t); }} className="p-1.5 text-muted-foreground hover:text-primary">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={(e) => remove(t.id, e)} className="p-1.5 text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editId ? "Editar inquilino" : "Nuevo inquilino"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setField("nombre", e.target.value)} data-testid="tenant-nombre-input" /></div>
            <div><Label>Apellidos</Label><Input value={form.apellidos} onChange={(e) => setField("apellidos", e.target.value)} /></div>
            <div><Label>Nacionalidad</Label><Input value={form.nacionalidad} onChange={(e) => setField("nacionalidad", e.target.value)} /></div>
            <div><Label>{tipoDocumentoLabel(form.tipo_documento)}</Label><Input value={form.dni} onChange={(e) => setField("dni", e.target.value)} /></div>
            <div className="sm:col-span-2">
              <Label>Tipo de documento</Label>
              <RadioGroup
                className="grid-flow-col auto-cols-max gap-4 mt-1.5"
                value={form.tipo_documento || "DNI"}
                onValueChange={(v) => setField("tipo_documento", v)}
              >
                {TIPOS_DOCUMENTO.map((o) => (
                  <div key={o.value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={o.value} id={`tipo-doc-${o.value}`} />
                    <Label htmlFor={`tipo-doc-${o.value}`} className="font-normal cursor-pointer">{o.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="sm:col-span-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => setField("direccion", e.target.value)} /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setField("telefono", e.target.value)} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setField("email", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-tenant-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
